import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { resolveBackendAssetUrl } from '../services/apiService';
import {
  compareMachinesByHeightProximity,
  formatHeightMeters,
  getMachineCardSpecs,
  getMachineDrive,
  getMachineHeight,
  machineMeetsMinimumHeight,
  parseHeightInput,
} from '../utils/catalogSpecs';

function MachineCard({ machine }) {
  const specs = getMachineCardSpecs(machine);
  const imageUrl = resolveBackendAssetUrl(machine.imageUrl);
  const datasheetUrl = resolveBackendAssetUrl(machine.datasheetUrl);
  return (
    <article className="machine-card">
      {imageUrl
        ? <img className="machine-image" src={imageUrl} alt={machine.title} loading="lazy" />
        : <div className="machine-image-placeholder" aria-label="Kein Maschinenbild">SR</div>}
      <div className="machine-body">
        <span className="machine-category">{machine.category?.familyName ? `${machine.category.familyName} · ` : ''}{machine.category?.name || 'Maschine'}</span>
        <span className="machine-type">Typ: {machine.model}</span>
        <h3>{machine.title}</h3>
        <p className="machine-description">{machine.description || `${machine.manufacturer} ${machine.model} für Ihren nächsten Einsatz.`}</p>
        {specs.length > 0 && (
          <ul className="spec-list">
            {specs.map((spec) => (
              <li key={spec.key}>
                <span>{spec.label}</span>
                <strong>{spec.value}{spec.unit ? ` ${spec.unit}` : ''}</strong>
              </li>
            ))}
          </ul>
        )}
        <div className="machine-actions">
          <Link className="card-button" to={`/anfrage?maschine=${machine.id}`}>Jetzt anfragen</Link>
          {datasheetUrl
            ? <a className="card-button secondary" href={datasheetUrl} target="_blank" rel="noreferrer">Datenblatt</a>
            : <span className="card-button secondary disabled" title="Für Dummydaten ist kein PDF hinterlegt">Datenblatt</span>}
        </div>
      </div>
    </article>
  );
}

export default function HomePage() {
  const [machines, setMachines] = useState([]);
  const [categories, setCategories] = useState([]);
  const [familyName, setFamilyName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [driveValue, setDriveValue] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([api.get('/catalog/machines'), api.get('/catalog/categories')])
      .then(([machineResponse, categoryResponse]) => {
        setMachines(machineResponse.data);
        setCategories(categoryResponse.data);
      })
      .catch(() => setError('Der Maschinenkatalog konnte gerade nicht geladen werden.'))
      .finally(() => setLoading(false));
  }, []);

  const familyOptions = useMemo(() => Array.from(new Set(
    categories.map((category) => category.familyName).filter(Boolean)
  )).sort((a, b) => a.localeCompare(b, 'de')), [categories]);

  const productGroupOptions = useMemo(() => categories.filter((category) => (
    !familyName || category.familyName === familyName
  )), [categories, familyName]);

  const heightMachines = useMemo(() => (
    machines.filter((machine) => (!familyName || machine.category?.familyName === familyName)
      && (!categoryId || String(machine.categoryId) === String(categoryId)))
  ), [machines, familyName, categoryId]);

  const requestedHeight = parseHeightInput(heightInput);
  const heightInputInvalid = Boolean(heightInput.trim()) && requestedHeight === null;

  const heightFieldLabel = useMemo(() => {
    const kinds = new Set(heightMachines.map((machine) => getMachineHeight(machine)?.kind).filter(Boolean));
    if (kinds.size === 1 && kinds.has('working')) return 'Arbeitshöhe';
    if (kinds.size === 1 && kinds.has('lift')) return 'Hubhöhe';
    return 'Arbeits-/Hubhöhe';
  }, [heightMachines]);

  const driveMachines = useMemo(() => {
    const matches = heightMachines.filter((machine) => (
      requestedHeight === null || machineMeetsMinimumHeight(machine, requestedHeight)
    ));
    return requestedHeight === null
      ? matches
      : matches.sort((first, second) => compareMachinesByHeightProximity(first, second, requestedHeight));
  }, [heightMachines, requestedHeight]);

  const driveOptions = useMemo(() => {
    const unique = new Map();
    driveMachines.forEach((machine) => {
      const drive = getMachineDrive(machine);
      if (drive && !unique.has(drive.value)) unique.set(drive.value, drive);
    });
    return Array.from(unique.values()).sort((first, second) => first.label.localeCompare(second.label, 'de'));
  }, [driveMachines]);

  const filtered = useMemo(() => {
    const matches = machines.filter((machine) => {
      if (familyName && machine.category?.familyName !== familyName) return false;
      if (categoryId && String(machine.categoryId) !== String(categoryId)) return false;
      if (requestedHeight !== null && !machineMeetsMinimumHeight(machine, requestedHeight)) return false;
      const drive = getMachineDrive(machine);
      if (driveValue && drive?.value !== driveValue) return false;
      const height = getMachineHeight(machine);
      const heightTerms = height
        ? `${height.label} ${height.meters} ${height.kind === 'working' ? 'Arbeitshöhe' : 'Hubhöhe'}`
        : '';
      const haystack = `${machine.title} ${machine.manufacturer} ${machine.model} ${machine.typeLabel || ''} ${heightTerms} ${drive?.label || ''}`.toLocaleLowerCase('de-DE');
      return haystack.includes(search.trim().toLocaleLowerCase('de-DE'));
    });
    return requestedHeight === null
      ? matches
      : matches.sort((first, second) => compareMachinesByHeightProximity(first, second, requestedHeight));
  }, [machines, familyName, categoryId, requestedHeight, driveValue, search]);

  const resetSelection = () => {
    setFamilyName('');
    setCategoryId('');
    setHeightInput('');
    setDriveValue('');
    setSearch('');
  };

  return (
    <>
      <section className="hero">
        <div className="hero-inner">
          <p className="eyebrow">Maschinenmiete auf die smarte ART</p>
          <h1>Die richtige Maschine. Passende Vermieter. Eine Anfrage.</h1>
          <p className="hero-copy">
            Bedarf, Einsatzort und Mietdauer angeben – SmartRent leitet Ihre Anfrage gezielt an Vermieter weiter, die genau diesen Maschinentyp anbieten.
          </p>
          <div className="hero-actions">
            <Link className="button-primary" to="/anfrage">Mietanfrage starten</Link>
            <a className="button-secondary" href="#maschinen">Maschinen entdecken</a>
          </div>
        </div>
      </section>

      <section className="process-strip" aria-label="So funktioniert SmartRent">
        <div className="process-step"><span className="step-number">1</span><div><strong>Maschine wählen</strong><span>Technische Daten direkt aus dem Hersteller-Katalog vergleichen.</span></div></div>
        <div className="process-step"><span className="step-number">2</span><div><strong>Bedarf beschreiben</strong><span>Einsatzort, Zeitraum, Menge und gewünschte Anzahl an Preisinformationen angeben.</span></div></div>
        <div className="process-step"><span className="step-number">3</span><div><strong>Preisinformationen erhalten</strong><span>Passende Vermieter kalkulieren; die anonymen Preisinformationen kommen per E-Mail.</span></div></div>
      </section>

      <section className="content-section" id="maschinen">
        <div className="section-heading">
          <div><p className="eyebrow" style={{ color: '#0b766e' }}>Maschinenkatalog</p><h2>Für jeden Einsatz passend.</h2></div>
          <p></p>
        </div>
        <div className="catalog-selector">
          <div className="selector-header">
            <div>
              <span className="selector-kicker">Maschinenfinder</span>
              <strong>In vier Schritten zur passenden Mietmaschine</strong>
            </div>
            {(familyName || categoryId || heightInput || driveValue || search) && <button type="button" onClick={resetSelection}>Auswahl zurücksetzen</button>}
          </div>
          <div className="selector-grid">
            <label>
              <span><b>1</b> Familie</span>
              <select
                aria-label="Familie auswählen"
                value={familyName}
                onChange={(event) => {
                  setFamilyName(event.target.value);
                  setCategoryId(''); setHeightInput(''); setDriveValue('');
                }}
              >
                <option value="">Alle Familien</option>
                {familyOptions.map((family) => <option value={family} key={family}>{family}</option>)}
              </select>
            </label>
            <label>
              <span><b>2</b> Produktgruppe</span>
              <select
                aria-label="Produktgruppe auswählen"
                value={categoryId}
                onChange={(event) => {
                  setCategoryId(event.target.value);
                  setHeightInput(''); setDriveValue('');
                }}
              >
                <option value="">Alle Produktgruppen</option>
                {productGroupOptions.map((category) => <option value={category.id} key={category.id}>{category.name} ({category.machineCount})</option>)}
              </select>
            </label>
            <div className="selector-step selector-height-step">
              <label htmlFor="catalog-minimum-height"><span><b>3</b> {heightFieldLabel} (mind.)</span></label>
              <div className="height-input-shell">
                <input
                  id="catalog-minimum-height"
                  aria-label={`${heightFieldLabel} als Mindesthöhe in Metern eingeben`}
                  aria-invalid={heightInputInvalid}
                  inputMode="decimal"
                  placeholder="Alle Höhen oder z. B. 12,8"
                  value={heightInput}
                  onChange={(event) => {
                    setHeightInput(event.target.value);
                    setDriveValue('');
                  }}
                />
                <span>m</span>
              </div>
              {heightInputInvalid && <small className="height-input-error">Bitte eine positive Höhe eingeben, z. B. 12,8.</small>}
              <small className="height-input-help">Leer lassen für alle Höhen. Komma und Punkt sind als Dezimaltrennzeichen möglich.</small>
            </div>
            <label>
              <span><b>4</b> Antrieb</span>
              <select
                aria-label="Antrieb auswählen"
                value={driveValue}
                onChange={(event) => setDriveValue(event.target.value)}
              >
                <option value="">Alle Antriebe</option>
                {driveOptions.map((drive) => <option value={drive.value} key={drive.value}>{drive.label}</option>)}
              </select>
            </label>
          </div>
          <div className="selector-search">
            <input aria-label="Maschinen suchen" placeholder="Alternativ nach Hersteller, Modell oder Höhe suchen" value={search} onChange={(event) => setSearch(event.target.value)} />
            <span>
              {filtered.length} Mietmaschine{filtered.length === 1 ? '' : 'n'} gefunden
              {requestedHeight !== null ? ` · ab ${formatHeightMeters(requestedHeight)} m` : ''}
            </span>
          </div>
        </div>
        {loading && <div className="empty-state">Maschinenkatalog wird geladen …</div>}
        {error && <div className="empty-state">{error}</div>}
        {!loading && !error && filtered.length === 0 && (
          <div className="empty-state">
            {familyName || categoryId || heightInput || driveValue || search
              ? 'Für diese Auswahl wurde keine passende Mietmaschine gefunden.'
              : 'Noch keine veröffentlichten Maschinen vorhanden.'}
          </div>
        )}
        <div className="machine-grid">
          {filtered.map((machine) => <MachineCard machine={machine} key={machine.id} />)}
        </div>
      </section>
    </>
  );
}
